import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { auth, api } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { t, currentLang, setLang } from "@/lib/i18n";
import { Delete, LogIn } from "lucide-react";

const PIN_LEN = 4;

export default function Login() {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (auth.getToken()) {
      api.verify().then(() => nav("/", { replace: true })).catch(() => auth.clear());
    }
  }, [nav]);

  useEffect(() => {
    const onKey = (e) => {
      if (busy) return;
      if (/^\d$/.test(e.key)) push(e.key);
      else if (e.key === "Backspace") pop();
      else if (e.key === "Enter") submit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pin, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const push = (d) => {
    setError("");
    setPin((p) => (p.length >= 8 ? p : p + d));
  };
  const pop = () => {
    setError("");
    setPin((p) => p.slice(0, -1));
  };
  const submit = async () => {
    if (pin.length < PIN_LEN || busy) return;
    setBusy(true);
    try {
      const res = await api.login(pin);
      auth.setToken(res.token);
      toast.success(t.login.welcome);
      nav("/", { replace: true });
    } catch {
      setError(t.login.wrongPin);
      setPin("");
      toast.error(t.login.wrongPin);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (pin.length >= PIN_LEN) {
      const t2 = setTimeout(submit, 150);
      return () => clearTimeout(t2);
    }
  }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"];

  return (
    <div className="min-h-screen bg-slate-50 bg-grid flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8 justify-center relative">
          <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center text-white font-display font-bold text-lg shadow-lg shadow-emerald-500/30">
            {currentLang === "en" ? "T" : "Т"}
          </div>
          <div>
            <div className="font-display font-semibold text-slate-900 text-lg leading-none">
              {t.nav.brand}
            </div>
            <div className="text-xs text-slate-500 mt-1">{t.nav.brandTag}</div>
          </div>
          <div className="absolute right-0 top-0 flex items-center gap-0.5 rounded-lg bg-white border border-slate-200 p-0.5">
            <button
              onClick={() => currentLang !== "ru" && setLang("ru")}
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md transition-colors ${
                currentLang === "ru" ? "bg-emerald-500 text-white" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              RU
            </button>
            <button
              onClick={() => currentLang !== "en" && setLang("en")}
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md transition-colors ${
                currentLang === "en" ? "bg-emerald-500 text-white" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              EN
            </button>
          </div>
        </div>

        <div className="card-soft p-8">
          <h1 className="font-display text-2xl font-semibold text-slate-900 text-center">
            {t.login.title}
          </h1>
          <p className="text-sm text-slate-500 text-center mt-1">
            {t.login.subtitle}
          </p>

          <div className="flex items-center justify-center gap-4 mt-8 mb-6">
            {Array.from({ length: PIN_LEN }).map((_, i) => (
              <div
                key={i}
                data-testid={AUTH.pinDigit(i)}
                className={`h-3.5 w-3.5 rounded-full border transition-colors duration-150 ${
                  i < pin.length
                    ? "bg-emerald-500 border-emerald-500"
                    : "bg-white border-slate-300"
                }`}
              />
            ))}
          </div>

          {error && (
            <div
              data-testid={AUTH.pinError}
              className="text-center text-sm text-rose-600 mb-4"
            >
              {error}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            {keys.map((k, idx) => {
              if (k === "") return <div key={idx} />;
              if (k === "back") {
                return (
                  <button
                    key={idx}
                    data-testid={AUTH.pinBackspace}
                    onClick={pop}
                    className="pin-key h-14 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center"
                    aria-label="backspace"
                  >
                    <Delete className="h-5 w-5" />
                  </button>
                );
              }
              return (
                <button
                  key={idx}
                  data-testid={AUTH.pinKey(k)}
                  onClick={() => push(k)}
                  className="pin-key h-14 rounded-2xl bg-white border border-slate-200 hover:bg-slate-50 hover:border-emerald-300 text-slate-900 font-display text-xl"
                >
                  {k}
                </button>
              );
            })}
          </div>

          <button
            data-testid={AUTH.pinSubmit}
            onClick={submit}
            disabled={pin.length < PIN_LEN || busy}
            className="mt-6 w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <LogIn className="h-4 w-4" />
            {busy ? t.login.checking : t.login.unlock}
          </button>

          <div className="mt-4 text-center text-xs text-slate-400">
            {t.login.hint}
          </div>
        </div>
      </div>
    </div>
  );
}
